import OrderDataService from './order.service';

export type OrderTableStatus = 'order' | 'waiting' | 'not_found' | 'error';

/**
 * Result of order/table path processing
 */
export interface OrderTableResult {
  status: OrderTableStatus;
  orderId: string | number | null;
  currentURL: string;
  isDemo?: boolean;
  error?: string;
}

const asOrderList = (payload: unknown): { id?: string | number; is_demo?: boolean }[] => {
  return Array.isArray(payload) ? payload : [];
};

/**
 * Service for combined order and table operations
 */
class OrderTableService {
  /**
   * Process URL path segments and handle table/order interactions
   * @param path1 First path segment (o, t, etc.)
   * @param path2 Second path segment (order ID, table number, etc.)
   */
  async processOrderTablePath(path1: string, path2: string): Promise<OrderTableResult> {
    if (path1 === 'o') {
      return this.handleDirectOrderAccess(path2);
    }
    if (path1 === 't') {
      return this.handleTableAccess(path2);
    }
    return { status: 'not_found', orderId: null, currentURL: '404' };
  }

  /**
   * Handle direct order access via "o/[id]" path
   */
  private async handleDirectOrderAccess(orderId: string): Promise<OrderTableResult> {
    try {
      const response = await OrderDataService.get(orderId);
      const order = response.data;

      if (!order || !order.id) {
        return { status: 'not_found', orderId: null, currentURL: '404' };
      }

      return {
        status: 'order',
        orderId,
        currentURL: `/o/${orderId}`,
        isDemo: Boolean(order.is_demo),
      };
    } catch (error) {
      console.error('Error fetching order:', error);
      return { status: 'not_found', orderId: null, currentURL: '404', error: String(error) };
    }
  }

  /**
   * Handle a table QR via "t/[number]".
   * Looks up an existing open check only. Never creates an order.
   */
  private async handleTableAccess(tableNumber: string): Promise<OrderTableResult> {
    if (!/^\d+$/.test(tableNumber)) {
      return { status: 'not_found', orderId: null, currentURL: '404' };
    }

    try {
      const orderResponse = await OrderDataService.getOrderFromTable(tableNumber);
      const orders = asOrderList(orderResponse.data);
      const order = orders.find((entry) => entry && entry.id != null);

      if (order && order.id != null) {
        return {
          status: 'order',
          orderId: order.id,
          currentURL: `/o/${order.id}`,
          isDemo: Boolean(order.is_demo),
        };
      }

      return {
        status: 'waiting',
        orderId: null,
        currentURL: `/t/${tableNumber}`,
      };
    } catch (error) {
      console.error('Error fetching order for table:', error);
      return { status: 'error', orderId: null, currentURL: 'error', error: String(error) };
    }
  }
}

const orderTableService = new OrderTableService();

export default orderTableService;
